/**
 * UserDataTab — silnik formatek (keystone unifikacja-formatek — mirror
 * `ContractorDataTab.tsx`/`CompanyDataTab.tsx`). Pola core routowane przez
 * `renderField(fieldId, settings)` z propsów, dostarczany przez rodzica (`UserDetail`)
 * z `useKartotekaRenderField(userEngineConfig, runtime)`. Ten komponent pobiera TYLKO
 * aktywny layout modułu Użytkownicy przez `useSystemModuleActiveLayout('users')`
 * (ten sam hook co rodzic — honoruje layout przypięty w pozycji menu `?mi=`),
 * iteruje po sekcjach z `layout_config.tabs[0].sections` (zakładka „Dane" type='fields')
 * i dla każdego elementu typu 'field' deleguje do `renderField` (silnik sam rozstrzyga
 * core-vs-ext, metadane pola, gcrecord, custom widget vs natywną delegację) — DataTab
 * nie zna już `SystemModuleField`/`fieldsQ`.
 *
 * Quirk zachowany z przed-silnikowej wersji (loop-level, NIE renderer — silnik go nie
 * zna, bo dotyczy WYŁĄCZNIE pól DODANYCH): konto techniczne (api/service) w trybie create
 * NIE dostaje pól dodatkowych — endpoint POST /users nie przyjmuje `extValues` dla
 * api/service, więc pokazanie pól dodatkowych byłoby cichą utratą wpisanych danych po
 * submit. `fields` (ta sama lista, którą silnik zwraca z `useKartotekaRenderField`) daje
 * DataTab dostęp do `isCore` per `el.fieldId`, żeby ta bramka mogła zadziałać PRZED
 * wywołaniem `renderField`.
 *
 * Save flow w UserDetail (rodzic): JEDEN PATCH /users/:id z polami core, `extValues` i
 * `additionalTables` w tym samym body (create człowieka: POST /users/invite z `extValues`) —
 * bez osobnego PUT ext-fields.
 */

import { Fragment, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecordLanguage } from '@saas/shared';

import { tryRenderLayoutElement } from '@/components/fields/LayoutElementRegistry';
import { type ContextRenderers, PLACEHOLDER_TYPES } from '@/components/form/RecursiveFormRenderer';
import { isFieldElementVisible, StyledFieldSlot } from '@/components/form/styled-field';
import { Section } from '@/components/kartoteki/CoreFieldLayout';
import type { EngineFormField } from '@/components/kartoteki/engine/engine-types';
import { Skeleton } from '@/components/Skeleton';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { loadTabsFromConfig } from '@/pages/system-modules/layout-shape-adapter';

const MODULE_SLUG = 'users';

// ─── Type exports (używane w UserDetail) ───────────────────────────────────

export interface UserFormData {
  // Edytowalne (PATCH /api/users/:id)
  displayName: string | null;
  language: RecordLanguage;
  defaultCompanyId: string | null;
  // Readonly display (zarządzane przez inne endpointy)
  email: string;
  username: string;
  userType: 'human' | 'api' | 'service' | 'impersonation';
  isSuperUser: boolean;
  status: 'active' | 'invited' | 'suspended';
  lastLoginAt: string | null;
  invitedAt: string | null;
  /**
   * Grupy rekordu — kody słownika `user_groups`, WIELE na rekord (migracja 0117).
   * Klasyfikacja do filtrowania listy (chipy). Edytowalne przez PATCH /api/users/:id.
   */
  groups: string[];
  /**
   * Kategoria rekordu — jedna, opcjonalna, z tabeli `record_categories` (kartoteka_key='users',
   * migracja 0130). DYSTYNKTYWNA od `groups` (wiele, słownik). Edytowalna przez PATCH /api/users/:id.
   */
  categoryId: string | null;
  /**
   * Pracownik powiązany z kontem (id z kartoteki pracowników) — tylko edycja, `null` = brak /
   * odpięcie. Edytowalny przez PATCH /api/users/:id (`employeeId`).
   */
  employeeId: string | null;
}

export const EMPTY_USER_FORM: UserFormData = {
  displayName: null,
  language: 'pl',
  defaultCompanyId: null,
  email: '',
  username: '',
  userType: 'human',
  isSuperUser: false,
  status: 'active',
  lastLoginAt: null,
  invitedAt: null,
  groups: [],
  categoryId: null,
  employeeId: null,
};

/**
 * Słownik slug→stateKey dla pól core użytkownika (mirror `CONTRACTOR_CORE_FIELD_SLUGS` /
 * `COMPANY_CORE_FIELD_SLUGS`). Slugi = `coreFields` modułu `users` w
 * `apps/api/lib/seed-system-modules.ts`. Służy do budowy `Record<slug, wartość>` na potrzeby
 * warunków widoczności pól/bloków z kreatora ORAZ (silnik formatek) do budowy
 * `userEngineConfig.fields` z tego samego źródła prawdy.
 */
export const USER_CORE_FIELD_SLUGS: Record<string, keyof UserFormData> = {
  email: 'email',
  username: 'username',
  display_name: 'displayName',
  language: 'language',
  user_type: 'userType',
  is_super_user: 'isSuperUser',
  default_company_id: 'defaultCompanyId',
  status: 'status',
  last_login_at: 'lastLoginAt',
  invited_at: 'invitedAt',
  groups: 'groups',
  category_id: 'categoryId',
  employee_id: 'employeeId',
};

/** Buduje `Record<slug, wartość>` z formularza użytkownika (do oceny warunków widoczności). */
export function buildUserCoreRecordValues(form: UserFormData): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [slug, key] of Object.entries(USER_CORE_FIELD_SLUGS)) {
    result[slug] = form[key];
  }
  return result;
}

// ─── Types dla fetch'owanego layoutu (metadane pól żyją teraz w silniku) ────

interface LayoutElement {
  id: string;
  type: string;
  fieldId?: string;
  /** Ukrycie przez nakładkę per-tenant (systemowy layout) — pomijane na karcie. */
  hidden?: boolean;
  settings?: Record<string, unknown>;
}
interface LayoutSection {
  id: string;
  title: string | null;
  columns?: number;
  elements: LayoutElement[];
}

// ─── Props ─────────────────────────────────────────────────────────────────

export interface UserDataTabProps {
  /**
   * Renderery elementów zależnych od kontekstu, te same, które UserDetail przekazuje do
   * RecursiveFormRenderer. Bez nich taki element postawiony w zakładce „Dane" renderuje się
   * jako martwy placeholder z generycznego rejestru. Most do czasu przejścia kartoteki w
   * całości na silnik (mirror `ContractorDataTabProps`/`CompanyDataTabProps`). Dziś Użytkownicy
   * nie mają żadnego takiego bloku w seedzie zakładki „Dane", ale prop zostaje dla parytetu
   * z resztą kartotek na silniku.
   */
  contextRenderers?: ContextRenderers;
  /**
   * Renderuje pole core/ext po id — dostarczany przez rodzica z
   * `useKartotekaRenderField(userEngineConfig, runtime)`. Silnik sam rozstrzyga core-vs-ext,
   * metadane pola (gcrecord/isCore/isRequired) i custom widget vs natywną delegację — ten
   * komponent tylko iteruje po layoucie.
   */
  renderField: (fieldId: string, settings?: Record<string, unknown>) => ReactNode;
  /**
   * Metadane pól core+ext z silnika (`useKartotekaRenderField` zwraca `fields`) — WYŁĄCZNIE do
   * loop-level bramki ext-suppression (isCore per `el.fieldId`), patrz nagłówek pliku. Silnik
   * sam i tak sprawdza core/ext wewnątrz `renderField` — to jest DRUGI, niezależny check,
   * potrzebny TYLKO Użytkownikom (Kontrahenci/Firmy go nie mają).
   */
  fields: EngineFormField[];
  /**
   * Wartości formularza keyed po slugu pola (core + ext) — do oceny warunków widoczności
   * pól/bloków (`settings.conditionalField/Operator/Value`). Rodzic (`UserDetail`) buduje ją
   * z `buildUserCoreRecordValues(form)` + `extValues`. Default `{}` — bez niej warunki
   * traktujemy jak „zawsze widoczne".
   */
  formData?: Record<string, unknown>;
  /** Czy formatka jest w trybie tworzenia rekordu — steruje bramką ext-suppression. */
  isCreate: boolean;
  /** Typ konta z bieżącego stanu formularza — steruje bramką ext-suppression (patrz nagłówek). */
  userType: UserFormData['userType'];
}

// ─── Główny komponent ──────────────────────────────────────────────────────

export function UserDataTab({
  contextRenderers,
  renderField,
  fields,
  formData = {},
  isCreate,
  userType,
}: UserDataTabProps) {
  const { t } = useTranslation();

  // Aktywny layout — TEN SAM hook co rodzic: honoruje layout przypięty w pozycji menu
  // (`?mi=` → `layoutId`) i sam nakłada nakładkę per-tenant (`applyTabOverrides`).
  // Metadane pól żyją w silniku (useKartotekaRenderField, rodzic), ten komponent zna
  // tylko strukturę sekcji.
  const layoutQ = useSystemModuleActiveLayout(MODULE_SLUG);

  const { dataTabSections, dataTabExtras } = useMemo(() => {
    // F12.N post-E1.T7: nowy rekurencyjny kształt rozpakowywany przez adapter. Nakładkę
    // per-tenant (ukryte pola/formatki) nakłada już hook — tu NIE aplikujemy jej drugi raz.
    const cfg = layoutQ.layoutConfig ?? { elements: [] };
    const tabs = loadTabsFromConfig(cfg);
    const dataTab = tabs.find((tab) => tab.type === 'fields');
    return {
      dataTabSections: (dataTab?.sections ?? []) as LayoutSection[],
      // Elementy „luźne" zakładki Dane spoza sekcji (mirror ContractorDataTab/CompanyDataTab —
      // dziś Użytkownicy nie mają takiego bloku w seedzie, ale adapter jest wspólny, więc
      // trzymamy tę samą obsługę, żeby ewentualny loose block z buildera nie zniknął po cichu).
      dataTabExtras: (dataTab?.extras ?? []) as LayoutElement[],
    };
  }, [layoutQ.layoutConfig]);

  // Indeks metadanych pól (z silnika) po id — WYŁĄCZNIE do bramki ext-suppression niżej.
  const fieldById = useMemo(() => {
    const map = new Map<string, EngineFormField>();
    for (const f of fields) map.set(f.id, f);
    return map;
  }, [fields]);

  // Renderuje „luźny" element layoutu (blok-widok spoza sekcji) PEŁNĄ szerokością: przez
  // contextRenderers rodzica, a dla elementów bez kontekstu — przez generyczny rejestr
  // (heading/alert/…). Settings elementu lecą do renderera.
  function renderLooseElement(el: LayoutElement): ReactNode {
    const contextRenderer = PLACEHOLDER_TYPES.has(el.type)
      ? contextRenderers?.[el.type]
      : undefined;
    if (contextRenderer) {
      return (
        <Fragment key={el.id}>
          {contextRenderer({ element: { id: el.id, type: el.type, settings: el.settings } })}
        </Fragment>
      );
    }
    const layoutNode = tryRenderLayoutElement({
      element: {
        id: el.id,
        type: el.type,
        settings: (el.settings ?? {}) as Record<string, unknown>,
      },
      children: [],
      mode: 'form',
    });
    return layoutNode ? <Fragment key={el.id}>{layoutNode}</Fragment> : null;
  }

  if (layoutQ.isLoading) {
    return (
      <div className="space-y-3 p-4" data-testid="user-data-tab-loading">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  const showLayoutWarning = layoutQ.isError || (!layoutQ.isLoading && dataTabSections.length === 0);

  return (
    <div className="space-y-6" data-testid="user-data-tab">
      {showLayoutWarning && (
        <div
          className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
          data-testid="user-data-tab-no-layout"
        >
          {t('tenant.users.detail.noLayoutWarning')}
        </div>
      )}

      {dataTabSections.map((section, sectionIdx) => (
        <Fragment key={section.id}>
          <Section title={section.title ?? t('tenant.users.detail.untitledSection')}>
            {section.elements.map((el) => {
              // Ukrycie przez nakładkę per-tenant (systemowy layout) — pomiń pole/element z `hidden`.
              if (el.hidden === true) return null;
              // Warunek widoczności z kreatora (`settings.conditionalField/Operator/Value`
              // vs `formData`) — parytet z bogatym rendererem.
              if (!isFieldElementVisible(el, formData)) return null;
              // Non-field elementy (heading/divider/alert/...) renderowane przez
              // LayoutElementRegistry; elementy zależne od kontekstu idą przez contextRenderers
              // rodzica — dokładnie jak w silniku (RecursiveFormRenderer) i mirror
              // ContractorDataTab/CompanyDataTab.
              if (el.type !== 'field') {
                const contextRenderer = PLACEHOLDER_TYPES.has(el.type)
                  ? contextRenderers?.[el.type]
                  : undefined;
                if (contextRenderer) {
                  return (
                    <div key={el.id} className="md:col-span-2">
                      {contextRenderer({
                        element: { id: el.id, type: el.type, settings: el.settings },
                      })}
                    </div>
                  );
                }
                const layoutNode = tryRenderLayoutElement({
                  element: {
                    id: el.id,
                    type: el.type,
                    settings: (el.settings ?? {}) as Record<string, unknown>,
                  },
                  children: [],
                  mode: 'form',
                });
                return layoutNode ? <Fragment key={el.id}>{layoutNode}</Fragment> : null;
              }

              if (!el.fieldId) return null;

              // Konto techniczne (api/service) w trybie create — endpoint POST /users NIE
              // przyjmuje pól dodatkowych; nie pokazujemy ich, żeby user nie wypełnił wartości,
              // które i tak by przepadły (cicha utrata danych). Dotyczy WYŁĄCZNIE pól DODANYCH
              // (ext) — core routing (np. `username` → null dla non-human) żyje w rendererze.
              const field = fieldById.get(el.fieldId);
              if (isCreate && userType !== 'human' && field && !field.isCore) return null;

              // Styl pola z kreatora (kolor etykiety/kontrolki) — scoped-CSS wokół
              // `renderField`, wspólny klocek z bogatym rendererem (styled-field).
              return (
                <StyledFieldSlot key={el.id} el={el} formData={formData}>
                  {renderField(el.fieldId, el.settings)}
                </StyledFieldSlot>
              );
            })}
          </Section>
          {/* Luźne elementy zakładki Dane spoza sekcji — patrz komentarz `dataTabExtras` wyżej. */}
          {sectionIdx === 0 && dataTabExtras.map(renderLooseElement)}
        </Fragment>
      ))}
      {/* Nietypowy layout bez żadnej sekcji — luźne elementy i tak pokaż. */}
      {dataTabSections.length === 0 && dataTabExtras.map(renderLooseElement)}
    </div>
  );
}
